import { type ReactNode } from 'react'
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl'

import { BanknotesIcon } from '@gto/icons-react/outline/banknotes'
import { CurrencyDollarIcon } from '@gto/icons-react/outline/currency-dollar'
import { PresentationChartLineIcon } from '@gto/icons-react/outline/presentation-chart-line'
import { ShoppingCartIcon } from '@gto/icons-react/outline/shopping-cart'
import { cn } from '@gto/ui/cn'
import { Paper } from '@gto/ui/paper'
import { ToggleButton, ToggleButtonGroup } from '@gto/ui/toggle-button'
import { Typography } from '@gto/ui/typography'

import type { AffiliateAdminPartnerDetailSchema } from '@/api/gen'
import { useGetPartnerAnalyticsView, useGetPartnerStatsView } from '@/api/gen'
import { DashboardKpiTile } from '@/components/dashboard-kpi-tile'
import { InlineQueryError } from '@/components/inline-query-error'
import { PerformanceChart } from '@/components/performance-chart'
import { useReportQueryError } from '@/hooks/use-report-query-error'
import { isGranularity, useGranularitySnapshot } from '@/utils/granularity'
import { toAmount } from '@/utils/to-amount'
import { toNullablePct } from '@/utils/to-nullable-pct'

import { BalanceCorrectionCard } from '../cards/balance-correction-card'
import { PayoutInfoCard } from '../cards/payout-info-card'
import { PersonalInfoCard } from '../cards/personal-info-card'
import { TierCommissionCard } from '../cards/tier-commission-card'

// Flattens SectionCard's standalone-box chrome so each card renders as a
// section of the unified Account-details surface: no own border/rounding, no
// width cap, no hover accent, and grow to fill the column so the divider
// grid stays rectangular.
const FLAT_SECTION = 'max-w-none grow rounded-none border-0 bg-transparent hover:border-transparent'

type OverviewTabProps = {
  partner: AffiliateAdminPartnerDetailSchema
  partnerId: string
}

export const OverviewTab = ({ partner, partnerId }: OverviewTabProps) => {
  const { formatMessage } = useIntl()
  // Snapshot the date window so `getDateRange()`'s `new Date()` runs on toggle,
  // not every render — keeps the TanStack Query key identity stable across
  // unrelated rerenders.
  const { granularity, dateRange, setGranularity } = useGranularitySnapshot('monthly')

  // Pass the granularity-derived range so the BE can compute the *_delta_pct
  // fields (it only does so when both dates are supplied); balances stay
  // current server-side regardless of the range.
  const statsQuery = useGetPartnerStatsView(partnerId, dateRange)
  const {
    data: stats,
    isLoading: statsLoading,
    isError: statsError,
    refetch: refetchStats,
  } = statsQuery
  const analyticsQuery = useGetPartnerAnalyticsView(partnerId, {
    ...dateRange,
    granularity,
  })
  const { data: analytics } = analyticsQuery

  // Forward load failures to Sentry — the bare app QueryClient has no global sink.
  // The inline error surface below only covers the stats query; analytics failures
  // would otherwise silently render an empty chart, so report both. Both fire on
  // TERMINAL failure only so retries don't multiply Sentry events.
  useReportQueryError(statsQuery, 'getPartnerStatsView failed: ')
  useReportQueryError(analyticsQuery, 'getPartnerAnalyticsView failed: ')

  // Analytics rows arrive newest-first; charts and sparklines read oldest-first.
  const analyticsRows = [...(analytics?.rows ?? [])].reverse()
  const ordersSeries = analyticsRows.map((row) => row.orders)
  const revenueSeries = analyticsRows.map((row) =>
    toAmount(row.revenue_usd, 'partner-detail analytics revenue_usd:'),
  )
  const commissionsSeries = analyticsRows.map((row) =>
    toAmount(row.commission_usd, 'partner-detail analytics commission_usd:'),
  )

  const balanceAvailable = toAmount(
    stats?.balance_available_usd,
    'partner-detail balance_available_usd:',
  )
  const balanceLocked = toAmount(stats?.balance_locked_usd, 'partner-detail balance_locked_usd:')
  const totalRevenue = toAmount(stats?.total_revenue_usd, 'partner-detail total_revenue_usd:')
  const totalCommission = toAmount(
    stats?.total_commission_usd,
    'partner-detail total_commission_usd:',
  )

  const revenueDelta = toNullablePct(
    stats?.revenue_delta_pct,
    'partner-detail toNullablePct revenue_delta_pct:',
  )
  const commissionDelta = toNullablePct(
    stats?.commission_delta_pct,
    'partner-detail toNullablePct commission_delta_pct:',
  )
  const ordersDelta = toNullablePct(
    stats?.orders_delta_pct,
    'partner-detail toNullablePct orders_delta_pct:',
  )
  const totalOrders = stats?.total_orders ?? 0

  const chartData = analyticsRows.map((row) => ({
    label: row.period_label,
    revenue: toAmount(row.revenue_usd, 'partner-detail analytics revenue_usd:'),
    commissions: toAmount(row.commission_usd, 'partner-detail analytics commission_usd:'),
  }))

  if (statsError) {
    return (
      <div className="flex flex-col gap-3">
        <InlineQueryError
          data-tst="overview-stats-error"
          messageId="admin.partnerDetail.overview.error"
          onRetry={() => void refetchStats()}
        />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-row flex-wrap gap-2">
        <DashboardKpiTile
          deltaPct={revenueDelta}
          icon={<CurrencyDollarIcon />}
          label={<FormattedMessage id="dashboard.totalRevenue" />}
          loading={statsLoading}
          muted={totalRevenue === 0 && revenueDelta == null}
          sparklineData={revenueSeries}
          value={
            <FormattedNumber
              currency="USD"
              maximumFractionDigits={0}
              style="currency"
              value={totalRevenue}
            />
          }
        />
        <DashboardKpiTile
          deltaPct={commissionDelta}
          icon={<BanknotesIcon />}
          label={<FormattedMessage id="admin.partnerDetail.commission" />}
          loading={statsLoading}
          muted={totalCommission === 0 && commissionDelta == null}
          sparklineData={commissionsSeries}
          value={
            <FormattedNumber
              currency="USD"
              maximumFractionDigits={0}
              style="currency"
              value={totalCommission}
            />
          }
        />
        <DashboardKpiTile
          deltaPct={ordersDelta}
          icon={<ShoppingCartIcon />}
          label={<FormattedMessage id="dashboard.totalOrders" />}
          loading={statsLoading}
          muted={totalOrders === 0 && ordersDelta == null}
          sparklineData={ordersSeries}
          value={<FormattedNumber value={totalOrders} />}
        />
        <BalanceCard
          available={balanceAvailable}
          loading={statsLoading}
          locked={balanceLocked}
        />
      </div>

      <PerformanceChart
        data={chartData}
        emptyLabel={<FormattedMessage id="admin.partnerDetail.chart.empty" />}
        // 176px: the chart's job here is trend shape — values live in the KPI
        // tiles — and the shorter card is what keeps Overview above the fold.
        height={176}
        icon={<PresentationChartLineIcon />}
        title={<FormattedMessage id="dashboard.performance" />}
        controls={
          <ToggleButtonGroup
            size="small"
            value={granularity}
            onValueChange={(v) => {
              if (isGranularity(v)) {
                setGranularity(v)
              }
            }}
          >
            <ToggleButton value="daily">
              {formatMessage({ id: 'dashboard.granularity.daily' })}
            </ToggleButton>
            <ToggleButton value="weekly">
              {formatMessage({ id: 'dashboard.granularity.weekly' })}
            </ToggleButton>
            <ToggleButton value="monthly">
              {formatMessage({ id: 'dashboard.granularity.monthly' })}
            </ToggleButton>
          </ToggleButtonGroup>
        }
      />

      {/* One unified surface, not floating boxes: the three sections sit in a
          single bordered card, split by hairline vertical dividers — denser
          than stacked sections and short enough to keep Overview above the
          fold. No heading above: the section titles already say what they
          are. Uneven section heights read as a table thanks to the dividers.
          Screens are desktop-first (md: = up to 1024px), where it collapses
          to one column with horizontal dividers. */}
      <Paper
        className="grid grid-cols-3 divide-x divide-divider overflow-hidden rounded-md border border-divider md:grid-cols-1 md:divide-x-0 md:divide-y"
        elevation="2"
      >
        <PersonalInfoCard
          className={FLAT_SECTION}
          partner={partner}
          partnerId={partnerId}
        />
        <TierCommissionCard
          className={FLAT_SECTION}
          partner={partner}
          partnerId={partnerId}
        />
        <PayoutInfoCard
          className={FLAT_SECTION}
          partner={partner}
          partnerId={partnerId}
        />
      </Paper>

      {/* Migration tooling — admin-only, kept separate from the read-only
          Account Details surface above because its action (write a manual
          commission row) is a heavier mutation than the inline cards' PATCH. */}
      <BalanceCorrectionCard partnerId={partnerId} />
    </div>
  )
}

type BalanceCardProps = {
  available: number
  locked: number
  loading: boolean
}

const BalanceCard = ({ available, locked, loading }: BalanceCardProps) => (
  <Paper
    className="flex min-w-[140px] flex-[1_1_0px] flex-col gap-3 rounded-md bg-gradient-primary-vertical p-4 md:flex-[1_0_calc(50%-4px)] sm:flex-[1_0_100%]"
    elevation="2"
  >
    <Typography
      // tracking-[0.06em] matches the dashboard-kpi-tile eyebrow.
      className="text-xs font-medium uppercase tracking-[0.06em]"
      color="text.secondary"
      variant="body2"
    >
      <FormattedMessage id="admin.partnerDetail.currentBalance" />
    </Typography>
    {loading ? null : (
      <>
        {/* The headline is what the partner can withdraw RIGHT NOW — UNLOCKED, not-yet-paid
            commissions (balance_available_usd). NOT available + locked. */}
        <Typography
          className="font-heading font-semibold leading-100"
          variant="h4"
        >
          <FormattedNumber
            currency="USD"
            maximumFractionDigits={2}
            minimumFractionDigits={2}
            style="currency"
            value={available}
          />
        </Typography>
        <Typography
          color="text.secondary"
          variant="caption"
        >
          <FormattedMessage id="admin.partnerDetail.currentBalance.hint" />
        </Typography>
        <div className="mt-auto flex flex-col gap-0.5">
          <BalanceSplitRow
            color="bg-warning-main"
            label={<FormattedMessage id="dashboard.lockedBalance" />}
            value={locked}
          />
          <BalanceSplitRow
            color="bg-info-main"
            label={<FormattedMessage id="admin.partnerDetail.futureBalance" />}
            value={available + locked}
          />
        </div>
      </>
    )}
  </Paper>
)

type BalanceSplitRowProps = {
  color: string
  label: ReactNode
  value: number
}

const BalanceSplitRow = ({ color, label, value }: BalanceSplitRowProps) => (
  <div className="flex items-center justify-between gap-2">
    <span className="inline-flex items-center gap-1">
      <span className={cn('size-2 rounded-full', color)} />
      <Typography
        color="text.secondary"
        variant="caption"
      >
        {label}
      </Typography>
    </span>
    <Typography
      className="font-mono tabular-nums"
      variant="caption"
    >
      <FormattedNumber
        currency="USD"
        maximumFractionDigits={2}
        minimumFractionDigits={2}
        style="currency"
        value={value}
      />
    </Typography>
  </div>
)
